import { lazy, Suspense, useLayoutEffect, type ReactNode } from 'react';
import { Route } from 'react-router';

/**
 * The SPA's public pages, as <Route> elements.
 *
 * Declared here rather than inline in routes.tsx because two routers render
 * them: the browser's (routes.tsx, inside the full app) and the build-time
 * prerender (entry-server.tsx), which renders ONLY these so the HTML a crawler
 * fetches contains the page. Both must produce the same DOM for hydration to
 * line up, so the elements live in one place.
 *
 * Still lazy on the client: a signed-in user never loads the landing page. On a
 * hard load of a prerendered page, React keeps the server HTML on screen until
 * the chunk arrives, then hydrates it in place.
 */
const LandingPage = lazy(() => import('./LandingPage').then((m) => ({ default: m.LandingPage })));
const DownloadPage = lazy(() =>
  import('./DownloadPage').then((m) => ({ default: m.DownloadPage }))
);
const MobilePage = lazy(() => import('./MobilePage').then((m) => ({ default: m.MobilePage })));
const PhoneScanPage = lazy(() =>
  import('@/features/imports/PhoneScanPage').then((m) => ({ default: m.PhoneScanPage }))
);
const DemoPage = lazy(() =>
  import('@/features/demo/DemoPage').then((m) => ({ default: m.DemoPage }))
);
const HelpPage = lazy(() =>
  import('@/features/help/HelpPage').then((m) => ({ default: m.HelpPage }))
);
const TermsPage = lazy(() =>
  import('@/features/legal/TermsPage').then((m) => ({ default: m.TermsPage }))
);
const PrivacyPage = lazy(() =>
  import('@/features/legal/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
);
const SubprocessorsPage = lazy(() =>
  import('@/features/legal/SubprocessorsPage').then((m) => ({ default: m.SubprocessorsPage }))
);

/*
  AppShell provides the Suspense boundary for every route inside it. The public
  routes have no shell above them, so they carry their own. The fallback is a
  blank white page rather than a spinner: these chunks are small, and a spinner
  that flashes for 40ms on a marketing page looks worse than nothing.
*/
export function Public({ children }: { children: ReactNode }) {
  // The marketing pages are white in every theme, and the ground under them
  // (html/body, visible on overscroll and until the chunk arrives) has to be
  // too. index.html adds the same class before first paint on a hard load, and
  // the prerendered pages carry it in their <html> tag; this keeps it right
  // across client-side navigation in and out.
  useLayoutEffect(() => {
    document.documentElement.classList.add('marketing');
    return () => document.documentElement.classList.remove('marketing');
  }, []);
  return <Suspense fallback={<div className="min-h-screen bg-white" />}>{children}</Suspense>;
}

export const marketingRoutes = (
  <>
    <Route
      path="/landing"
      element={
        <Public>
          <LandingPage />
        </Public>
      }
    />
    <Route
      path="/download"
      element={
        <Public>
          <DownloadPage />
        </Public>
      }
    />
    <Route
      path="/mobile"
      element={
        <Public>
          <MobilePage />
        </Public>
      }
    />
    {/* Demo mode's front door. Public and in the marketing chrome, but not in
        meta.ts: it reads the session and the query string, so it is served by
        the shell rather than prerendered. */}
    {/* Scanning paper with a phone: opened from the QR code on the computer.
        No sign-in; the link's one-time token is the credential. */}
    <Route
      path="/scan/:token"
      element={
        <Public>
          <PhoneScanPage />
        </Public>
      }
    />
    <Route
      path="/demo"
      element={
        <Public>
          <DemoPage />
        </Public>
      }
    />
    {/* The help videos: public so they can be linked from anywhere. */}
    <Route
      path="/help"
      element={
        <Public>
          <HelpPage />
        </Public>
      }
    />
    {/* The legal pages. /terms and /privacy are prerendered (meta.ts); the
        subprocessor list is linked from the privacy page and served by the shell. */}
    <Route
      path="/terms"
      element={
        <Public>
          <TermsPage />
        </Public>
      }
    />
    <Route
      path="/privacy"
      element={
        <Public>
          <PrivacyPage />
        </Public>
      }
    />
    <Route
      path="/legal/subprocessors"
      element={
        <Public>
          <SubprocessorsPage />
        </Public>
      }
    />
  </>
);
